import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2Off, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type Role = 'admin' | 'registrations_operator' | 'content_editor';
type ManagedUser = {
  id: string;
  email: string;
  fullName: string | null;
  isActive: boolean;
  hasGoogleIdentity: boolean;
  localPasswordEnabled: boolean;
  roles: Role[];
};
const roles: Array<{ value: Role; label: string }> = [
  { value: 'registrations_operator', label: 'Registraties' },
  { value: 'content_editor', label: 'Website' },
  { value: 'admin', label: 'Administrator' },
];

export function UsersPage() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [newRoles, setNewRoles] = useState<Role[]>(['content_editor']);
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<ManagedUser[]>('/users'),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const create = useMutation({
    mutationFn: () => api.post<ManagedUser>('/users', { email, fullName: fullName || null, roles: newRoles }),
    onSuccess: () => {
      setEmail('');
      setFullName('');
      refresh();
      toast.success('Google-toegang goedgekeurd');
    },
    onError: error => toast.error(error.message),
  });
  const updateRoles = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: Role[] }) => api.put(`/users/${id}/roles`, { roles }),
    onSuccess: refresh,
    onError: error => toast.error(error.message),
  });
  const updateStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.patch(`/users/${id}/status`, { isActive }),
    onSuccess: refresh,
    onError: error => toast.error(error.message),
  });
  const unlinkGoogleIdentity = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}/google-identity`),
    onSuccess: () => {
      refresh();
      toast.success('Google-account ontkoppeld');
    },
    onError: error => toast.error(error.message),
  });
  const removeUser = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: refresh,
    onError: error => toast.error(error.message),
  });

  const toggle = (selected: Role[], role: Role) =>
    selected.includes(role) ? selected.filter(value => value !== role) : [...selected, role];
  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Gebruikers</h1>
        <p className="text-muted-foreground">
          Keur Google-accounts vooraf goed en beheer hun toegang tot het Backoffice.
        </p>
      </div>
      <form
        className="grid gap-3 rounded-lg border p-4 md:grid-cols-3"
        onSubmit={event => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Input
          value={email}
          onChange={event => setEmail(event.target.value)}
          placeholder="Google e-mailadres"
          type="email"
          required
        />
        <Input value={fullName} onChange={event => setFullName(event.target.value)} placeholder="Naam" />
        <div className="flex items-center gap-3 md:col-span-3">
          {roles.map(role => (
            <label key={role.value} className="flex items-center gap-1 text-sm">
              <Checkbox
                checked={newRoles.includes(role.value)}
                onCheckedChange={() => setNewRoles(toggle(newRoles, role.value))}
              />
              {role.label}
            </label>
          ))}
        </div>
        <Button className="justify-self-start" disabled={create.isPending || newRoles.length === 0}>
          Google-toegang goedkeuren
        </Button>
      </form>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="p-3">Gebruiker</th>
              <th className="p-3">Rollen</th>
              <th className="p-3">Status</th>
              <th className="p-3">Acties</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="p-3" colSpan={4}>
                  Laden…
                </td>
              </tr>
            ) : (
              users.map(user => (
                <UserRow
                  key={user.id}
                  user={user}
                  onRoles={roles => updateRoles.mutate({ id: user.id, roles })}
                  onStatus={isActive => updateStatus.mutate({ id: user.id, isActive })}
                  onUnlinkGoogle={() => unlinkGoogleIdentity.mutate(user.id)}
                  onDelete={() => removeUser.mutate(user.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UserRow({
  user,
  onRoles,
  onStatus,
  onUnlinkGoogle,
  onDelete,
}: {
  user: ManagedUser;
  onRoles: (roles: Role[]) => void;
  onStatus: (isActive: boolean) => void;
  onUnlinkGoogle: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-b last:border-0">
      <td className="p-3">
        <div>{user.fullName || user.email}</div>
        <div className="text-muted-foreground">{user.email}</div>
      </td>
      <td className="p-3">
        <RoleEditor key={user.roles.join(',')} initialRoles={user.roles} onRoles={onRoles} />
      </td>
      <td className="p-3">
        <div className="space-y-1">
          <Button size="sm" variant={user.isActive ? 'outline' : 'default'} onClick={() => onStatus(!user.isActive)}>
            {user.isActive ? 'Deactiveren' : 'Activeren'}
          </Button>
          <p className="text-muted-foreground">
            {user.hasGoogleIdentity ? 'Google gekoppeld' : 'Google nog niet gekoppeld'}
          </p>
          {user.localPasswordEnabled && <p className="text-muted-foreground">Noodwachtwoord actief</p>}
        </div>
      </td>
      <td className="p-3">
        <div className="flex gap-2">
          {user.hasGoogleIdentity && (
            <Button
              size="icon"
              variant="outline"
              aria-label={`Google-account van ${user.email} ontkoppelen`}
              title="Google-account ontkoppelen"
              onClick={() => {
                if (
                  window.confirm(
                    `Google-account van ${user.email} ontkoppelen? De gebruiker moet bij de volgende aanmelding opnieuw koppelen.`
                  )
                )
                  onUnlinkGoogle();
              }}
            >
              <Link2Off aria-hidden="true" />
            </Button>
          )}
          <Button
            size="icon"
            variant="destructive"
            aria-label={`Gebruiker ${user.email} verwijderen`}
            title="Gebruiker verwijderen"
            onClick={() => {
              if (window.confirm(`Gebruiker ${user.email} verwijderen?`)) onDelete();
            }}
          >
            <Trash2 aria-hidden="true" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function RoleEditor({ initialRoles, onRoles }: { initialRoles: Role[]; onRoles: (roles: Role[]) => void }) {
  const [selected, setSelected] = useState(initialRoles);
  const rolesChanged = selected.length !== initialRoles.length || selected.some(role => !initialRoles.includes(role));
  const toggle = (role: Role) =>
    setSelected(current => (current.includes(role) ? current.filter(value => value !== role) : [...current, role]));
  return (
    <div className="flex flex-wrap gap-3">
      {roles.map(role => (
        <label key={role.value} className="flex items-center gap-1">
          <Checkbox checked={selected.includes(role.value)} onCheckedChange={() => toggle(role.value)} />
          {role.label}
        </label>
      ))}
      <Button
        size="sm"
        variant="outline"
        disabled={!rolesChanged}
        className={rolesChanged ? undefined : 'opacity-50'}
        onClick={() => onRoles(selected)}
      >
        Opslaan
      </Button>
    </div>
  );
}
